import React, { useState, useEffect } from 'react';
import { useDashboard } from './DashboardContext';
import { streamApi } from '../../services/api';
import useVisiblePolling from '../../hooks/useVisiblePolling';
import {
  Radio,
  Lock,
  Clock,
  TrendingUp,
  CheckCircle,
  XCircle,
  Calendar,
  AlertTriangle
} from 'lucide-react';

const LiveStreamPage = () => {
  const {
    user,
    streamData,
    countdown,
    nextSessionDate,
    setActivePage
  } = useDashboard();

  const [schedule, setSchedule] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(true);

  // Fetch weekly schedule (initial load)
  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const data = await streamApi.getSchedule();
        setSchedule(data.schedule || []);
      } catch (error) {
        console.error('Failed to fetch schedule:', error);
      } finally {
        setLoadingSchedule(false);
      }
    };
    fetchSchedule();
  }, []);

  // Refresh schedule every 60s while tab is visible (pauses in background).
  useVisiblePolling(async () => {
    const data = await streamApi.getSchedule();
    setSchedule(data.schedule || []);
  }, { intervalMs: 60000 });

  // Get this week and next week's schedule
  const thisWeek = schedule.slice(0, 7);
  const nextWeek = schedule.slice(7, 14);
  
  // Determine which week to show by default
  // Show next week starting Friday 6pm Eastern, through the weekend
  const now = new Date();
  
  // Calculate Eastern Time offset accounting for DST
  const getEasternOffset = () => {
    // Use Intl API to properly detect if Eastern Time is in DST
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(now);
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    const tzAbbr = tzPart ? tzPart.value : 'EST';
    
    // EDT = Daylight Time (UTC-4), EST = Standard Time (UTC-5)
    return tzAbbr === 'EDT' ? -4 : -5;
  };
  
  const easternOffset = getEasternOffset();
  const utcHours = now.getUTCHours();
  const easternHours = (utcHours + easternOffset + 24) % 24;
  const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday, 5 = Friday
  
  // Show next week if:
  // - It's Saturday (6) or Sunday (0)
  // - It's Friday (5) and after 6pm Eastern
  const isFridayAfter6pm = dayOfWeek === 5 && easternHours >= 18;
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const shouldShowNextWeek = isWeekend || isFridayAfter6pm;
  
  const [showNextWeek, setShowNextWeek] = useState(shouldShowNextWeek);

  // Get the displayed week based on toggle selection
  const displayedWeek = showNextWeek ? nextWeek : thisWeek;

  // Get upcoming cancellations for the currently displayed week (show all canceled days)
  const upcomingCancellations = displayedWeek.filter(day => 
    day.status === 'cancelled' && day.is_trading_day
  );

  // Format next session date for display
  const formatNextSessionDate = () => {
    if (!nextSessionDate) return 'Mon-Fri';
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[nextSessionDate.getDay()]}, ${months[nextSessionDate.getMonth()]} ${nextSessionDate.getDate()}`;
  };

  // Check if user has access to live trading (not Free tier and not Indicator-only)
  const hasLiveAccess = user?.membership_name && 
    user.membership_name !== 'Free' && 
    user.membership_name !== 'HyperDrive Indicator';

  if (!hasLiveAccess) {
    return (
      <div className="page-content">
        <h1 className="page-title">Live Trading Session</h1>
        <p className="page-description">Join Coach CJ's live trading session</p>
        
        <div className="access-restricted-card">
          <div className="restricted-icon">
            <Lock size={64} />
          </div>
          <h2 className="restricted-title">Upgrade Required</h2>
          <p className="restricted-text">
            Live trading sessions are available for paid members only. Upgrade your membership to join Coach CJ's daily live trading sessions.
          </p>
          <div className="restricted-features">
            <h4>With Live Trading access, you get:</h4>
            <ul>
              <li><CheckCircle size={16} /> Live execution trading with Coach CJ</li>
              <li><CheckCircle size={16} /> NQ/MNQ Futures trading</li>
              <li><CheckCircle size={16} /> Real-time trade alerts</li>
              <li><CheckCircle size={16} /> Daily sessions Mon-Fri at 9:20 AM ET</li>
            </ul>
          </div>
          <button 
            className="upgrade-now-btn"
            onClick={() => setActivePage('Membership')}
          >
            <TrendingUp size={20} />
            Upgrade Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <h1 className="page-title">Live Trading Session</h1>
      <p className="page-description">Join Coach CJ's live trading session</p>
      
      {/* Upcoming Cancellations Alert - Show first if any */}
      {upcomingCancellations.length > 0 && (
        <div className="upcoming-cancellations-alert">
          <AlertTriangle size={20} />
          <div className="cancellation-alert-content">
            <strong>Upcoming Canceled Sessions:</strong>
            <ul>
              {upcomingCancellations.map(day => (
                <li key={day.date}>
                  <span className="cancel-date">{day.day_name}, {day.month} {day.day_num}</span>
                  {day.cancellation_reason && <span className="cancel-reason"> — {day.cancellation_reason}</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Weekly Schedule - Above countdown */}
      <div className="weekly-schedule-card">
        <div className="schedule-header">
          <Calendar size={20} />
          <h3>{showNextWeek ? "Next Week's Schedule" : "This Week's Schedule"}</h3>
          <div className="week-toggle">
            <button 
              className={!showNextWeek ? 'active' : ''} 
              onClick={() => setShowNextWeek(false)}
            >
              This Week
            </button>
            <button 
              className={showNextWeek ? 'active' : ''} 
              onClick={() => setShowNextWeek(true)}
            >
              Next Week
            </button>
          </div>
        </div>
        {loadingSchedule ? (
          <p className="loading-text">Loading schedule...</p>
        ) : (
          <div className="user-schedule-grid">
            {displayedWeek.map((day) => (
              <div 
                key={day.date}
                className={`user-day-card ${day.status} ${day.is_today ? 'today' : ''}`}
              >
                <span className="user-day-name">{day.day_short}</span>
                <span className="user-day-num">{day.day_num}</span>
                {day.status === 'scheduled' && <span className="user-day-icon scheduled">✓</span>}
                {day.status === 'cancelled' && (
                  <>
                    <span className="user-day-icon cancelled">✕</span>
                    {day.cancellation_reason && (
                      <span className="user-cancel-reason">{day.cancellation_reason}</span>
                    )}
                  </>
                )}
                {day.status === 'off' && <span className="user-day-icon off">—</span>}
                {day.status === 'live' && <span className="user-day-icon live">LIVE</span>}
                {day.status === 'past' && <span className="user-day-icon past">—</span>}
                {day.is_today && <span className="user-today-label">TODAY</span>}
              </div>
            ))}
          </div>
        )}
        <div className="schedule-legend">
          <span><span className="legend-dot green"></span> Scheduled</span>
          <span><span className="legend-dot red"></span> Cancelled</span>
          <span><span className="legend-dot gray"></span> Off Day</span>
        </div>
      </div>

      {/* Countdown Timer */}
      <div className="trading-session-card" style={{ marginBottom: '2rem' }}>
        <div className="session-content">
          <div className="session-left">
            <div className="session-icon">
              <Clock size={24} />
            </div>
            <div className="session-info">
              <p className="session-label">Next Trading Session</p>
              <h3 className="session-title">Coach CJ's Live Stream</h3>
            </div>
          </div>
          <div className="session-right">
            <div className="session-countdown">
              <p className="countdown-label">Starts in</p>
              <div className="countdown-timer">{countdown}</div>
              <p className="countdown-schedule">9:20 AM ET • {formatNextSessionDate()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Session Cancelled State */}
      {!streamData.isTodayScheduled && (
        <div className="session-cancelled-card">
          <div className="session-cancelled-icon">
            <XCircle size={64} />
          </div>
          <h2 className="session-cancelled-title">Today's Session Cancelled</h2>
          <p className="session-cancelled-message">
            {streamData.cancellationMessage || "Today's trading session has been cancelled. See you next session!"}
          </p>
          <div className="next-session-box">
            <p className="next-session-label">NEXT SESSION</p>
            <p className="next-session-days">{formatNextSessionDate()}</p>
            <p className="next-session-time">9:20 AM ET</p>
          </div>
        </div>
      )}

      {/* Live State */}
      {streamData.isTodayScheduled && streamData.isLive && (
        <div className="session-live-card">
          <div className="session-live-icon">
            <Radio size={64} />
          </div>
          <div className="live-indicator-large">
            <span className="live-dot-large"></span>
            <h2 className="session-live-title">We're Live!</h2>
          </div>
          <p className="session-live-text">
            Coach CJ is currently hosting a live trading session. Click below to join the Zoom meeting.
          </p>
          <button 
            className="join-zoom-btn"
            onClick={() => window.open(streamData.zoomLink, '_blank')}
          >
            <Radio size={20} />
            Join Live Session
          </button>
        </div>
      )}

      {/* Offline State (only show if scheduled but not live) */}
      {streamData.isTodayScheduled && !streamData.isLive && (
        <div className="session-offline-card">
          <div className="session-offline-icon">
            <Radio size={64} />
          </div>
          <h2 className="session-offline-title">Session Offline</h2>
          <p className="session-offline-text">
            Coach CJ is not currently streaming. Check back during trading hours to join the live session.
          </p>
          <div className="next-session-box">
            <p className="next-session-label">NEXT SESSION</p>
            <p className="next-session-days">{formatNextSessionDate()}</p>
            <p className="next-session-time">9:20 AM ET</p>
          </div>
        </div>
      )}

      <div className="how-it-works-card">
        <h3 className="how-it-works-title">How It Works</h3>
        <div className="how-it-works-steps">
          <div className="step-item">
            <span className="step-number">1</span>
            <span className="step-text">Wait for the countdown timer to reach zero</span>
          </div>
          <div className="step-item">
            <span className="step-number">2</span>
            <span className="step-text">When Coach CJ goes live, the 'Join Live Session' button will appear</span>
          </div>
          <div className="step-item">
            <span className="step-number">3</span>
            <span className="step-text">Click the button to join the live trading session on Zoom</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveStreamPage;
